import { useEffect, useState } from 'react';
import api from '@/utils/api';
import { UserProfile, ProfileFormData, FormErrors } from '../types';

export const useProfile = () => {
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [formData, setFormData] = useState<ProfileFormData>({
        name: '',
        phone: '',
        company: '',
        bio: '',
        address: '',
        city: '',
        country: '',
        linkedinUrl: '',
    });
    const [formErrors, setFormErrors] = useState<FormErrors>({});

    const fetchProfile = async () => {
        try {
            setIsLoading(true);
            setError('');
            const { data } = await api.get<UserProfile>('/users/profile');
            setProfile(data);
            setFormData({
                name: data.name || '',
                phone: data.phone || '',
                company: data.company || '',
                bio: data.bio || '',
                address: data.address || '',
                city: data.city || '',
                country: data.country || '',
                linkedinUrl: data.linkedinUrl || '',
            });
        } catch (err) {
            console.error('Failed to fetch profile:', err);
            setError('Failed to load profile. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchProfile();
    }, []);

    const validateField = (field: keyof ProfileFormData, value: string | number): string | undefined => {
        switch (field) {
            case 'name':
                if (typeof value === 'string') {
                    if (!value.trim()) return 'Name is required';
                    if (value.trim().length < 2) return 'Name must be at least 2 characters';
                    if (value.trim().length > 100) return 'Name must not exceed 100 characters';
                }
                break;
            case 'phone':
                if (typeof value === 'string' && value.trim() && value.trim().length > 20) {
                    return 'Phone number must not exceed 20 characters';
                }
                break;
            case 'company':
                if (typeof value === 'string' && value.trim().length > 100) {
                    return 'Company name must not exceed 100 characters';
                }
                break;
            case 'bio':
                if (typeof value === 'string' && value.trim().length > 500) {
                    return 'Bio must not exceed 500 characters';
                }
                break;
            case 'city':
            case 'country':
                if (typeof value === 'string' && value.trim() && value.trim().length > 100) {
                    return `${field.charAt(0).toUpperCase() + field.slice(1)} must not exceed 100 characters`;
                }
                break;
        }
        return undefined;
    };

    const validateForm = (): boolean => {
        const errors: FormErrors = {};
        errors.name = validateField('name', formData.name);
        errors.phone = validateField('phone', formData.phone);
        errors.company = validateField('company', formData.company);
        errors.bio = validateField('bio', formData.bio);
        errors.city = validateField('city', formData.city);
        errors.country = validateField('country', formData.country);
        errors.linkedinUrl = validateField('linkedinUrl', formData.linkedinUrl);

        setFormErrors(errors);
        return !Object.values(errors).some((error) => error !== undefined);
    };

    const handleEdit = () => {
        setIsEditing(true);
        setError('');
        setSuccess('');
        setFormErrors({});
    };

    const handleCancel = () => {
        if (profile) {
            setFormData({
                name: profile.name || '',
                phone: profile.phone || '',
                company: profile.company || '',
                bio: profile.bio || '',
                address: profile.address || '',
                city: profile.city || '',
                country: profile.country || '',
                linkedinUrl: profile.linkedinUrl || '',
            });
        }
        setIsEditing(false);
        setError('');
        setSuccess('');
        setFormErrors({});
    };

    const handleSave = async (onSuccessCallback?: () => void) => {
        try {
            setIsSaving(true);
            setError('');
            setSuccess('');

            if (!validateForm()) {
                setIsSaving(false);
                return;
            }

            const { data } = await api.put<UserProfile>('/users/profile', formData);
            setProfile(data);
            setIsEditing(false);
            setFormErrors({});
            setSuccess('Profile updated successfully');
            setTimeout(() => setSuccess(''), 3000);
            if (onSuccessCallback) onSuccessCallback();
        } catch (err) {
            console.error('Failed to update profile:', err);
            setError('Failed to update profile. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleInputChange = (field: keyof ProfileFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        const error = validateField(field, value);
        setFormErrors((prev) => ({ ...prev, [field]: error }));
    };

    const setProfileState = (newProfile: UserProfile) => {
        setProfile(newProfile);
    };

    return {
        profile,
        setProfile: setProfileState,
        isLoading,
        isEditing,
        isSaving,
        error,
        setError,
        success,
        setSuccess,
        formData,
        setFormData,
        formErrors,
        handleEdit,
        handleCancel,
        handleSave,
        handleInputChange,
    };
};
